'use client'

import { global } from '@store/index'
import classNames from 'classnames'
import { isFunction } from 'lodash-es'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useRecoilValue } from 'recoil'
import { FullscreenControl, ListViewControl, SatelliteControl, ZoomControl } from '../control'
import MapboxContext, { useMapboxStates } from './context'
import './index.scss'
const MapboxSatelliteLayerLinz = dynamic(() => import('../layer/satelliteLayerLinz'), { ssr: false })
const UnitaryPlanZoneLayer = dynamic(() => import('../layer/unitaryPlanZone'), { ssr: false })
const UndergroundServicesLayer = dynamic(() => import('../layer/undergroundServices'), { ssr: false })
const FloodAssessmentLayer = dynamic(() => import('../layer/floodAssessment'), { ssr: false })
const ContourLayer = dynamic(() => import('../layer/contour'), { ssr: false })

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESSTOKEN

/**
 * mapbox地图
 * @param {string} id 地图id
 * @param {number} lng 经度Longitude
 * @param {number} lat 纬度Latitude
 * @param {number} zoom 地图层级
 * @param {boolean} scrollZoom 是否允许滚轮缩放地图
 * @param {boolean} dragPan 是否允许拖动平移
 * @param {boolean} fullscreenControl 是否显示全屏按钮
 * @returns map
 */
export default function MapboxMap({
  id,
  lng,
  lat,
  zoom = 18,
  scrollZoom = true,
  dragPan = true,
  fullscreenControl = true,
  zoomControl = true,
  satelliteControl = false,
  listViewControl = false,
  children,
  className,
  onLoad,
  onResize,
  mapBeforeChildren,
}) {

  const { isMobile } = useRecoilValue(global)
  const mapStates = useMapboxStates(lng, lat, zoom)
  const { mapContainer, map, mapCenter, setMapCenter, setMapZoom, setMapBounds, setMapLoaded, layerVisible } = mapStates

  // 创建地图
  useEffect(() => {
    if (map.current) return
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: process.env.NEXT_PUBLIC_MAPBOX_STYLE,
      center: mapCenter,
      zoom,
      scrollZoom,
      dragPan
    })
  })

  // 绑定地图事件
  useEffect(() => {
    if (!map.current) return

    function getBounds(map) {
      let bounds = map.current.getBounds()
      let ne = bounds.getNorthEast()
      let sw = bounds.getSouthWest()
      return [sw.lng, ne.lng < 0 ? 180 : ne.lng, sw.lat, ne.lat]
    }

    function handleMove() {
      // center
      const center = map.current.getCenter()
      setMapCenter([center.lng, center.lat])

      // zoom
      setMapZoom(parseInt(map.current.getZoom()))

      // bounds
      setMapBounds(getBounds(map))
    }

    function handleLoad() {
      setMapLoaded(true)
      // bounds
      setMapBounds(getBounds(map))
      isFunction(onLoad) && onLoad(map.current)
    }
    map.current.on('move', handleMove)
    map.current.on('load', handleLoad)

    if (isFunction(onResize)) {
      map.current.on('resize', onResize)
    }

    return () => {
      if (isFunction(onResize)) {
        map.current.off('resize', onResize)
      }
      map.current.off('move', handleMove)
      map.current.off('load', handleLoad)
    }
  }, [map, setMapCenter, setMapZoom, setMapBounds, setMapLoaded, onLoad, onResize])

  // 更改地图属性
  useEffect(() => {
    if (!map.current) return

    map.current.scrollZoom[scrollZoom ? 'enable' : 'disable']()
  }, [map, scrollZoom])
  // 更改地图属性
  useEffect(() => {
    if (!map.current) return

    map.current.dragPan[dragPan ? 'enable' : 'disable']()
  }, [map, dragPan])

  return (
    <MapboxContext.Provider value={{
      ...mapStates,
      id,
      map: map.current,
      mapContainer: mapContainer.current
    }}>
      {mapBeforeChildren}
      <div className={classNames('flex-1 min-h-0', className)}>
        <div ref={mapContainer} className='h-full mapbox-map-wrap' />
      </div>
      {listViewControl && <ListViewControl></ListViewControl>}
      {fullscreenControl && <FullscreenControl scrollZoom={scrollZoom}></FullscreenControl>}
      {satelliteControl && <SatelliteControl></SatelliteControl>}
      {zoomControl && !isMobile && <ZoomControl></ZoomControl>}
      {
        layerVisible.satelliteLinz && <MapboxSatelliteLayerLinz></MapboxSatelliteLayerLinz>
      }
      {
        layerVisible.unitaryPlanZone && <UnitaryPlanZoneLayer></UnitaryPlanZoneLayer>
      }
      {
        layerVisible.undergroundServices && <UndergroundServicesLayer></UndergroundServicesLayer>
      }
      {
        layerVisible.floodAssessment && <FloodAssessmentLayer></FloodAssessmentLayer>
      }
      {
        layerVisible.contour && <ContourLayer></ContourLayer>
      }
      {children}
    </MapboxContext.Provider>
  )
}

MapboxMap.propTypes = {
  id: PropTypes.string.isRequired,
  lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export function MapboxMapSkeleton({
  height
}) {
  return (
    <section className='skeleton-wrap'>
      <span style={{ height }}></span>
    </section>
  )
}